import { useCallback, useMemo, useState } from "react";
import {
    AccrualImporterColumn,
    AccrualImporterGroupBy,
    AccrualImporterView,
    AccrualImporterViewSchema,
} from "@common/types/views/accrualImporterViews";
import { FiltersTypes } from "@common/types/filters/general";
import { ensureRequiredColumns } from "@hooks/view/ensureRequiredColumns";
import { useViewChangeHandler } from "@hooks/view/useViewChangeHandler";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import {
    AccrualDocumentImporterMode,
    getAccrualDocumentImporterConfig,
} from "./accrualDocumentImporter.lib";
import {
    getBuiltInAccrualImporterViews,
    getAccrualImporterPageViewConfig,
    getDefaultAccrualImporterLineItemView,
    getDefaultAccrualImporterView,
} from "./accrualImporterPageViewConfig";

const EMPTY_FILTERS: FiltersTypes = {};

export function useAccrualImporterPageView(mode: AccrualDocumentImporterMode) {
    const isInvoices = mode === "invoices";
    const viewCacheKey = isInvoices
        ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_IMPORTER_INVOICES_PAGE_VIEW_CACHE_KEY
        : WORKSPACE_USER_CACHE_KEYS.ACCRUAL_IMPORTER_BILLS_PAGE_VIEW_CACHE_KEY;
    const baseViewCacheKey = isInvoices
        ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_IMPORTER_INVOICES_PAGE_BASE_VIEW_ID_CACHE_KEY
        : WORKSPACE_USER_CACHE_KEYS.ACCRUAL_IMPORTER_BILLS_PAGE_BASE_VIEW_ID_CACHE_KEY;
    const [cachedView, setCachedView] =
        useWorkspaceUserCache<AccrualImporterView>(
            viewCacheKey,
            AccrualImporterViewSchema,
        );
    const [view, setView] = useState<AccrualImporterView>(() => {
        const fallback = getDefaultAccrualImporterView();
        if (!cachedView) {
            return fallback;
        }
        return {
            ...cachedView,
            columns: ensureRequiredColumns(
                cachedView.columns,
                fallback.columns,
                [AccrualImporterColumn.ACTIONS],
            ),
            options: {
                ...cachedView.options,
                selection: true,
                bulkActions: true,
            },
        };
    });
    const persistView = useViewChangeHandler({
        setView,
        setCachedView,
    });
    const onViewChange = useCallback(
        (nextView: AccrualImporterView) => {
            persistView({
                ...nextView,
                groupBy: nextView.groupBy ?? view.groupBy,
                columns:
                    (nextView.groupBy ?? view.groupBy) ===
                    AccrualImporterGroupBy.LINE_ITEM
                        ? view.columns
                        : nextView.columns,
            });
        },
        [persistView, view.columns, view.groupBy],
    );
    const storedGroupBy = view.groupBy ?? AccrualImporterGroupBy.DOCUMENT;
    const groupBy =
        storedGroupBy === AccrualImporterGroupBy.COUNTERPARTY
            ? AccrualImporterGroupBy.DOCUMENT
            : storedGroupBy;
    const setGroupBy = (nextGroupBy: AccrualImporterGroupBy) => {
        const nextView = { ...view, groupBy: nextGroupBy };
        setView(nextView);
        setCachedView(nextView);
    };
    const builtInViews = useMemo(
        () => getBuiltInAccrualImporterViews(mode),
        [mode],
    );
    const viewConfig = useMemo(() => {
        const config = getAccrualDocumentImporterConfig(mode);
        return getAccrualImporterPageViewConfig({
            date: isInvoices ? "Invoice date" : "Bill date",
            number: isInvoices ? "Invoice Number" : "Bill Number",
            counterparty: config.counterpartyColumnHeader,
            accrualAccount: isInvoices ? "Income account" : "Expense account",
            balanceAccount: isInvoices
                ? "Receivables account"
                : "Payables account",
        });
    }, [isInvoices, mode]);
    const tableView = useMemo(() => {
        if (groupBy === AccrualImporterGroupBy.LINE_ITEM) {
            return {
                ...view,
                columns: getDefaultAccrualImporterLineItemView(mode).columns,
            };
        }
        return view;
    }, [groupBy, mode, view]);

    return {
        view,
        tableView,
        onViewChange,
        groupBy,
        setGroupBy,
        viewConfig,
        builtInViews,
        baseViewCacheKey,
        defaultFilters: EMPTY_FILTERS,
    };
}
